import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { BrandMark, ThemeToggle, type ThemeMode } from '../components/Theme';

export default function Signup({
  onAuth,
  theme,
  onThemeChange,
}: {
  onAuth: (token: string) => void;
  theme: ThemeMode;
  onThemeChange: (theme: ThemeMode) => void;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [code, setCode] = useState('');
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!challengeId && password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    if (!challengeId && password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      const res = challengeId
        ? await api.verifySignup(challengeId, email, code)
        : await api.signup(email, password);
      if (res.challenge) {
        setChallengeId(res.challenge.challenge_id);
        setCode('');
        return;
      }
      if (!res.token) {
        throw new Error('Signup did not return a token');
      }
      localStorage.setItem('forge.token', res.token);
      onAuth(res.token);
      navigate('/onboarding');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg px-4">
      <div className="fixed right-4 top-4">
        <ThemeToggle value={theme} onChange={onThemeChange} />
      </div>
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 justify-center mb-8">
          <BrandMark size="lg" />
          <div className="text-xl font-semibold tracking-tight">GameForge</div>
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold mb-4">
            {challengeId ? 'Verify email' : 'Create account'}
          </h2>

          {error && <div className="error-banner mb-4">{error}</div>}

          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            {!challengeId ? (
              <>
                <div>
                  <label className="text-xs font-semibold text-fg-dim uppercase tracking-wider block mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    className="input w-full"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    autoFocus
                    autoComplete="email"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-fg-dim uppercase tracking-wider block mb-1">
                    Password
                  </label>
                  <input
                    type="password"
                    className="input w-full"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-fg-dim uppercase tracking-wider block mb-1">
                    Confirm password
                  </label>
                  <input
                    type="password"
                    className="input w-full"
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                </div>
              </>
            ) : (
              <div>
                <label className="text-xs font-semibold text-fg-dim uppercase tracking-wider block mb-1">
                  Code
                </label>
                <input
                  className="input w-full tracking-[0.3em]"
                  value={code}
                  onChange={e => setCode(e.target.value)}
                  required
                  autoFocus
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                />
              </div>
            )}
            <button type="submit" className="btn btn-primary w-full mt-2" disabled={loading}>
              {loading ? 'Creating account...' : challengeId ? 'Verify email' : 'Create account'}
            </button>
          </form>

          <div className="text-center text-sm text-fg-dim mt-4">
            Already have an account?{' '}
            <Link to="/login" className="text-brand hover:underline">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
